import React from 'react'
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  ListItemButton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import './GlobalMenu.css'
import { useNav } from '../../../App'
import { getMenuItemsForSubApp } from '../../../utils/AppUtil'
import { ReactComponent as HoneywellForgeLogo } from '../../../assets/Honeywell-Forge-logo.svg'
import { ReactComponent as HoneywellHeaderLogo } from '../../../assets/Logo-Header.svg'
import { type OperationPermissionState } from '../redux/operationPermissionSlice'
import { useSelector } from 'react-redux'
import { type RootState } from '../../../store'

export interface SubApp {
  id: string
  name: string
  route?: string // Internal routes
  href?: string // external
}

export interface App {
  id: string
  name: string
  subApps: SubApp[]
}

interface GlobalMenuProps {
  open: boolean
  onClose: () => void
}

const ADMIN_URL = typeof window._env_?.REACT_APP_FORGE_IAM_ADMIN_CARD === 'string' ? window._env_.REACT_APP_FORGE_IAM_ADMIN_CARD : ''

export const apps: App[] = [
  {
    id: 'AOH',
    name: 'AOH',
    subApps: [
      { id: 'isense', name: 'AMI Operation Hub', route: '/isense' },
      { id: 'datapub', name: 'Data Publisher', route: '/datapub' }
    ]
  },
  {
    id: 'ADMIN & SUPPORT',
    name: 'ADMIN & SUPPORT',
    subApps: [{ id: 'admin', name: 'Admin', href: ADMIN_URL }]
  }
]

export default function GlobalMenu (props: GlobalMenuProps): JSX.Element {
  const navigate = useNavigate()
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const { setActiveSubAppId, setLastActiveItemId, activeSubAppId, setMenuItems, setShowSidebar } = useNav()

  const handleSubAppClick = (sub: SubApp): void => {
    if (sub.href !== null && sub.href !== undefined) {
      window.open(sub.href, '_blank', 'noopener,noreferrer')
      return
    }
    if (sub.route !== null && sub.route !== undefined) {
      navigate(sub.route)
      setActiveSubAppId(sub.id)
      setLastActiveItemId('home')
      setMenuItems(getMenuItemsForSubApp(sub.id))
      setShowSidebar(true)
      props.onClose()
    }
  }
  return (
    <Drawer
      data-testid="global-menu-drawer"
      anchor='left'
      open={props.open}
      variant='temporary'
      onClose={props.onClose}
      PaperProps={{
        sx: {
          width: 340,
          color: '#fff',
          backgroundColor: ' #272727',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '10px'
        }
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div>
            <Box sx={{ height: 30, width: 30 }}>
              <HoneywellForgeLogo style={{ width: '100%', height: '100%' }} aria-label="Honeywell Forge logo" role="img"/>
            </Box>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box sx={{ height: 15 }}>
              <HoneywellHeaderLogo style={{ width: '100%', height: '100%' }} aria-label="Honeywell Header logo" role="img"/>
            </Box>
            <Typography data-testid='performance-text' variant="h6" sx={{ fontWeight: 500, letterSpacing: '0.08em', fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>
              Performance<sup>+</sup> for Utilities
            </Typography>
          </div>
        </div>
        <IconButton
          onClick={props.onClose}
          aria-label="Close global menu"
          sx={{
            padding: '4px',
            '& svg': { color: '#fff' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {apps.map(app => (
          <div key={app.id} style={{ marginBottom: 16 }}>
            {/* App Header */}
            <div style={{ color: '#F0F0F0', fontSize: '12px', fontWeight: 800, marginBottom: 8, letterSpacing: '2px', lineHeight: '16px', textTransform: 'capitalize', fontFamily: 'Honeywell Sans Web' }}>
              {app.name}
            </div>
            {/* SubApps */}
            <List disablePadding>
              {app.subApps.map((sub: SubApp) => {
                if (!operationPermissions.permissions.can_read_report_template && sub.id === 'datapub') {
                  return null
                }
                const active = activeSubAppId === sub.id
                return (
                  <ListItem key={sub.id} disablePadding>
                    <ListItemButton
                      data-testid={`subapp-${sub.id}`}
                      key={sub.id}
                      onClick={() => { handleSubAppClick(sub) }}
                      sx={{
                        position: 'relative',
                        borderRadius: '6px',
                        color: active ? '#fff' : '#D0D0D0',
                        fontWeight: active ? 700 : 500,
                        backgroundColor: active ? '#2a2a2a' : 'transparent',
                        paddingY: '10px',
                        paddingX: '12px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#3f3f3fff',
                          color: '#fff'
                        },
                        '&::before': active
                          ? {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: '10%',
                              bottom: '10%',
                              width: '3px',
                              bgcolor: '#3bb0ff',
                              borderRadius: '1px'
                            }
                          : {}
                      }}
                    >
                      <ListItemText primary={sub.name} primaryTypographyProps={{ fontSize: '16px', lineHeight: '24px', fontFamily: 'Honeywell Sans Web' }} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </div>
        ))}
      </div>
    </Drawer>
  )
}
